// src/components/ScheduleScreen.jsx
// Agenda premium: lista por dia + adicionar + editar + excluir + abrir controle de sessão.
// Rotas esperadas:
// - /schedule
// - /schedule/new
// - /schedule/edit       state { appointment }
// - /session-control     state { appointment }

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import LembreteSessaoConfigModal from "../components/LembreteSessaoConfigModal";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/schedule.scss";

import {
  AlertTriangle,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ExternalLink,
  Info,
  LoaderCircle,
  Mail,
  MapPin,
  Menu,
  Pencil,
  Plus,
  RefreshCw,
  Repeat2,
  Search,
  Send,
  Settings2,
  Trash2,
  Video,
  X,
} from "lucide-react";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const STATUS_LABEL = {
  pendente: "Pendente",
  confirmado: "Confirmado",
  cancelado: "Cancelado",
  aguardando_confirmacao: "Aguardando",
  baixado: "Encerrado",
  expirado: "Expirado",
  no_show: "No-show",
};


const NOTICE_TONE = {
  success: "success",
  warning: "warning",
  info: "info",
  neutral: "neutral",
};

function getNoticeTitle(type) {
  if (type === "success") return "Tudo certo";
  if (type === "warning") return "Atenção";
  if (type === "info") return "Aviso";
  return "Mensagem";
}

function normalizeCommunicationText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const CHANNEL_FAILURE_STATUS = new Set([
  "failed",
  "undelivered",
  "canceled",
  "cancelled",
  "error",
  "erro",
  "falhou",
]);

const CHANNEL_SUCCESS_STATUS = new Set([
  "created",
  "queued",
  "sent",
  "delivered",
  "read",
  "success",
  "sucesso",
  "ok",
  "enviado",
  "entregue",
  "lido",
  "encaminhado",
]);

function normalizeChannelKey(value = "") {
  const normalized = normalizeCommunicationText(value).replace(/[^a-z]/g, "");

  if (normalized.includes("whatsapp")) return "whatsapp";
  if (normalized.includes("email") || normalized.includes("mail")) return "email";

  return normalized;
}

function findChannelResult(payload, channel) {
  if (!payload || typeof payload !== "object") return null;

  const target = normalizeChannelKey(channel);

  const containers = [
    payload,
    payload?.resultados,
    payload?.resultado,
    payload?.canais,
    payload?.comunicacao,
    payload?.comunicacoes,
    payload?.envios,
    payload?.termo,
    payload?.lembrete,
    payload?.cobranca,
    payload?.recibo,
    payload?.termo?.resultados,
    payload?.lembrete?.resultados,
    payload?.cobranca?.resultados,
    payload?.recibo?.resultados,
    payload?.recibo?.comunicacao,
    payload?.recibo?.comunicacoes,
  ].filter(Boolean);

  for (const container of containers) {
    if (Array.isArray(container)) {
      for (const item of container) {
        if (!item || typeof item !== "object") continue;

        const nestedDirect =
          target === "whatsapp"
            ? item.whatsapp ||
              item.whatsApp ||
              item.resultadoWhatsapp ||
              item.whatsappResultado
            : item.email ||
              item.eMail ||
              item.resultadoEmail ||
              item.emailResultado;

        if (
          nestedDirect !== undefined &&
          nestedDirect !== null &&
          (typeof nestedDirect === "object" ||
            typeof nestedDirect === "boolean" ||
            CHANNEL_SUCCESS_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ) ||
            CHANNEL_FAILURE_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ))
        ) {
          return nestedDirect;
        }

        const key = normalizeChannelKey(
          item?.canal ||
            item?.channel ||
            item?.tipoCanal ||
            item?.tipo ||
            item?.nome
        );

        if (key === target) return item;
      }

      continue;
    }

    if (typeof container !== "object") continue;

    const direct =
      target === "whatsapp"
        ? container.whatsapp ||
          container.whatsApp ||
          container.resultadoWhatsapp ||
          container.whatsappResultado
        : container.email ||
          container.eMail ||
          container.resultadoEmail ||
          container.emailResultado;

    if (direct !== undefined && direct !== null) {
      return direct;
    }

    const ownKey = normalizeChannelKey(
      container?.canal ||
        container?.channel ||
        container?.tipoCanal
    );

    if (ownKey === target) {
      return container;
    }
  }

  return null;
}

function normalizeChannelResult(payload, channel) {
  const raw = findChannelResult(payload, channel);

  if (raw === null || raw === undefined) return null;

  if (typeof raw === "boolean") {
    return {
      channel,
      success: raw,
      failed: !raw,
      status: raw ? "sent" : "failed",
      error: "",
    };
  }

  if (typeof raw === "string") {
    const status = normalizeCommunicationText(raw).replace(/\s+/g, "_");

    return {
      channel,
      success: CHANNEL_SUCCESS_STATUS.has(status),
      failed: CHANNEL_FAILURE_STATUS.has(status),
      status,
      error: "",
    };
  }

  if (typeof raw !== "object") return null;

  const status = normalizeCommunicationText(
    raw.status ||
      raw.messageStatus ||
      raw.twilioStatus ||
      raw.situacao ||
      ""
  ).replace(/\s+/g, "_");

  const successFlag =
    raw.success ??
    raw.sucesso ??
    raw.ok ??
    raw.enviado ??
    raw.sent ??
    null;

  const requested =
    raw.solicitado ??
    raw.requested ??
    raw.processado ??
    true;

  const failed =
    requested !== false &&
    (successFlag === false || CHANNEL_FAILURE_STATUS.has(status));

  const success =
    requested !== false &&
    !failed &&
    (successFlag === true || CHANNEL_SUCCESS_STATUS.has(status));

  return {
    channel,
    success,
    failed,
    status,
    requested,
    error:
      raw.erroMensagem ||
      raw.erro ||
      raw.error ||
      raw.message ||
      "",
  };
}

function getChannelStatusLabel(result) {
  if (!result) return "";

  if (result.requested === false) return "Não solicitado";

  const status = normalizeCommunicationText(result.status).replace(/\s+/g, "_");

  if (result.failed) return "Falhou";

  if (result.channel === "whatsapp") {
    if (status === "read" || status === "lido") return "Lido";
    if (status === "delivered" || status === "entregue") return "Entregue";
    if (status === "sent" || status === "enviado") return "Enviado";
    if (
      status === "queued" ||
      status === "created" ||
      status === "encaminhado"
    ) {
      return "Encaminhado";
    }

    if (result.success) return "Encaminhado";
    return "Processando";
  }

  if (result.success) return "Enviado";
  if (result.failed) return "Falhou";

  return "Processando";
}

function buildCommunicationFeedback(payload, fallbackMessage = "") {
  const email = normalizeChannelResult(payload, "email");
  const whatsapp = normalizeChannelResult(payload, "whatsapp");
  const results = [email, whatsapp].filter(Boolean);

  if (!results.length) {
    return {
      hasChannels: false,
      tone: "success",
      message: fallbackMessage,
      details: [],
    };
  }

  const details = results.map((result) => {
    const label = result.channel === "whatsapp" ? "WhatsApp" : "E-mail";
    const statusLabel = getChannelStatusLabel(result);

    if (result.failed && result.error) {
      return `${label}: ${statusLabel} — ${result.error}`;
    }

    return `${label}: ${statusLabel}`;
  });

  const hasSuccess = results.some((item) => item.success);
  const hasFailure = results.some((item) => item.failed);

  return {
    hasChannels: true,
    tone: hasFailure ? (hasSuccess ? "warning" : "error") : "success",
    message: details.join(" • "),
    details,
  };
}

const AUTH_FAILURE_CODES = new Set([
  "TOKEN_EXPIRED",
  "TOKEN_INVALID",
  "TOKEN_NOT_FOUND",
  "UNAUTHORIZED",
  "AUTH_REQUIRED",
  "AUTHENTICATION_REQUIRED",
  "INVALID_TOKEN",
  "JWT_EXPIRED",
]);

function isAuthenticationFailure(response, payload = {}) {
  if (response?.status === 401) {
    return true;
  }

  if (response?.status !== 403) {
    return false;
  }

  const code = String(
    payload?.code ||
      payload?.errorCode ||
      payload?.error ||
      ""
  )
    .trim()
    .toUpperCase();

  return AUTH_FAILURE_CODES.has(code);
}

function getApiErrorMessage(payload, fallback) {
  return (
    payload?.message ||
    payload?.detail ||
    payload?.error ||
    fallback ||
    "Não foi possível concluir a operação."
  );
}



const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function pad2(value) {
  return String(value).padStart(2, "0");
}

function toISODate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
}

function formatDateBR(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "--/--/----";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function formatLongDateBR(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  }).format(date);
}

function formatHHMM(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "--:--";

  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function parseApiDate(item) {
  const raw = item?.dataHora || item?.data;

  if (!raw) return null;

  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getClientName(appointment) {
  return (
    appointment?.clienteId?.nome ||
    appointment?.cliente?.nome ||
    appointment?.nomeCliente ||
    "Cliente"
  );
}

function getServiceName(appointment) {
  return String(appointment?.servico || appointment?.tipoSessao || "").trim();
}

function getAppointmentStatus(appointment) {
  return String(appointment?.status || "pendente").trim();
}

function getRecurrenceInfo(appointment = {}) {
  const recurrenceId =
    appointment?.recorrenciaId ||
    appointment?.recorrencia?.id ||
    null;

  if (!recurrenceId) return null;

  const type = String(
    appointment?.recorrenciaTipo ||
      appointment?.recorrencia?.tipo ||
      "recorrente"
  ).toLowerCase();

  const labels = {
    semanal: "Semanal",
    quinzenal: "Quinzenal",
    mensal: "Mensal",
  };

  return {
    id: String(recurrenceId),
    label: labels[type] || "Recorrente",
    exception:
      appointment?.recorrenciaExcecao === true ||
      appointment?.recorrencia?.excecao === true,
  };
}

function getStatusLabel(status) {
  return STATUS_LABEL[status] || status || "Pendente";
}


const TERMINAL_APPOINTMENT_STATUS = new Set([
  "baixado",
  "cancelado",
  "expirado",
  "no_show",
]);

function isAppointmentOpen(appointment = {}) {
  return !TERMINAL_APPOINTMENT_STATUS.has(
    getAppointmentStatus(appointment)
  );
}

function getOperationalStatus(appointment = {}) {
  const status = getAppointmentStatus(appointment);

  if (status === "baixado") {
    return {
      key: "encerrado",
      label: "Sessão encerrada",
      tone: "success",
      hint: "Atendimento concluído e sessão registrada.",
    };
  }

  if (status === "cancelado") {
    return {
      key: "cancelado",
      label: "Cancelada",
      tone: "danger",
      hint: "Este agendamento foi cancelado.",
    };
  }

  if (status === "no_show") {
    return {
      key: "no_show",
      label: "No-show",
      tone: "danger",
      hint: "Cliente não compareceu ao atendimento.",
    };
  }

  if (status === "expirado") {
    return {
      key: "expirado",
      label: "Expirada",
      tone: "neutral",
      hint: "Este compromisso expirou sem encerramento.",
    };
  }

  return {
    key: "aberto",
    label: "Em aberto",
    tone: "warning",
    hint: "Sessão ainda precisa ser encerrada.",
  };
}

function getClientConfirmationState(appointment = {}) {
  const resposta = String(
    appointment?.clienteRespostaConfirmacao || ""
  )
    .trim()
    .toUpperCase();

  const via = String(
    appointment?.clienteRespostaConfirmacaoVia || ""
  )
    .trim()
    .toUpperCase();

  const confirmado =
    appointment?.confirmadoPeloCliente === true ||
    resposta === "CONFIRMADO";

  const naoComparecera = resposta === "NAO_COMPARECEREI";

  if (naoComparecera) {
    return {
      tipo: "nao_comparecerei",
      confirmado: false,
      naoComparecera: true,
      via,
      badge: "Não poderá comparecer",
      badgeTone: "warning",
      hint:
        via === "WHATSAPP"
          ? "O cliente informou pelo WhatsApp que não poderá comparecer. Avalie se deve cancelar ou reagendar o atendimento."
          : "O cliente informou que não poderá comparecer. Avalie se deve cancelar ou reagendar o atendimento.",
    };
  }

  if (confirmado) {
    return {
      tipo: "confirmado",
      confirmado: true,
      naoComparecera: false,
      via,
      badge:
        via === "WHATSAPP"
          ? "Presença confirmada"
          : "Presença confirmada",
      badgeTone: "client-confirmed",
      hint:
        via === "WHATSAPP"
          ? "O cliente confirmou a presença pelo WhatsApp."
          : via === "LINK"
            ? "O cliente confirmou a presença pelo link seguro enviado por e-mail."
            : "O cliente confirmou a presença.",
    };
  }

  return {
    tipo: "sem_resposta",
    confirmado: false,
    naoComparecera: false,
    via: "",
    badge: "",
    badgeTone: "",
    hint: "",
  };
}

function getAppointmentMode(appointment) {
  const service = getServiceName(appointment).toLowerCase();

  if (service.includes("online")) {
    return {
      label: "Online",
      icon: <Video size={21} aria-hidden="true" />,
      tone: "blue",
    };
  }

  return {
    label: "Presencial",
    icon: <MapPin size={21} aria-hidden="true" />,
    tone: "green",
  };
}

function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function isSameLocalDay(a, b) {
  return toISODate(a) === toISODate(b);
}




async function fetchGoogleCalendarStatus(token) {
  if (!token) {
    return {
      conectado: false,
      operacional: false,
      requerReconexao: false,
    };
  }

  try {
    const statusResponse = await fetch(
      `${API_BASE}/psy/integracoes/google/status`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    saveAuthToken(statusResponse.headers.get("x-new-token"));

    const statusData = await statusResponse.json().catch(() => ({}));

    if (!statusResponse.ok) {
      return {
        conectado: false,
        operacional: false,
        requerReconexao: false,
        erro:
          statusData?.message ||
          "Não foi possível consultar a integração com o Google.",
      };
    }

    if (!statusData?.conectado) {
      return {
        ...statusData,
        conectado: false,
        operacional: false,
        requerReconexao: false,
      };
    }

    const testResponse = await fetch(
      `${API_BASE}/psy/integracoes/google/testar`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    saveAuthToken(testResponse.headers.get("x-new-token"));

    const testData = await testResponse.json().catch(() => ({}));

    if (testResponse.ok) {
      return {
        ...statusData,
        ...testData,
        conectado: true,
        operacional: true,
        requerReconexao: false,
      };
    }

    return {
      ...statusData,
      conectado: true,
      operacional: false,
      requerReconexao: Boolean(testData?.requerReconexao),
      erro:
        testData?.message ||
        statusData?.ultimoErro ||
        "A conexão com o Google Agenda precisa ser revisada.",
    };
  } catch (error) {
    return {
      conectado: false,
      operacional: false,
      requerReconexao: false,
      erro:
        error?.message ||
        "Não foi possível consultar a integração com o Google.",
    };
  }
}

function getGoogleMeetLink(appointment = {}) {
  return String(
    appointment?.googleMeet?.link ||
      appointment?.hangoutLink ||
      ""
  ).trim();
}

function getGoogleSyncState(appointment = {}) {
  const google = appointment?.googleCalendar || {};

  if (google?.ultimoErro) {
    return {
      tone: "error",
      label: "Erro no Google",
      title: google.ultimoErro,
    };
  }

  if (google?.sincronizado && google?.eventId) {
    return {
      tone: "success",
      label: "No Google",
      title: "Evento sincronizado com o Google Agenda.",
    };
  }

  return {
    tone: "neutral",
    label: "Não sincronizado",
    title: "Este agendamento ainda não possui evento no Google Agenda.",
  };
}
function NoticeBanner({ notice, onClose }) {
  if (!notice) return null;

  const tone = NOTICE_TONE[notice.type] || NOTICE_TONE.neutral;

  return (
    <section className={`sch-notice sch-notice--${tone}`} role="status" aria-live="polite">
      <div className="sch-notice__icon" aria-hidden="true">
        {tone === "success" ? (
          <CheckCircle2 size={19} />
        ) : tone === "warning" ? (
          <AlertTriangle size={19} />
        ) : (
          <Info size={19} />
        )}
      </div>

      <div className="sch-notice__body">
        <strong>{notice.title || getNoticeTitle(tone)}</strong>
        <span>{notice.message}</span>
      </div>

      {notice.actionLabel && typeof notice.onAction === "function" ? (
        <button className="sch-notice__action" type="button" onClick={notice.onAction}>
          {notice.actionLabel}
        </button>
      ) : null}

      <button
        className="sch-notice__close"
        type="button"
        onClick={onClose}
        aria-label="Fechar aviso"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </section>
  );
}

function ConfirmModal({ open, appointment, deleting, onClose, onConfirm }) {
  if (!open) return null;

  const appointmentDate = parseApiDate(appointment);
  const name = getClientName(appointment);

  return (
    <div className="sch-modal-overlay" role="dialog" aria-modal="true">
      <div className="sch-modal">
        <div className="sch-modal__icon" aria-hidden="true">
          <AlertTriangle size={23} />
        </div>

        <div className="sch-modal__body">
          <h2>Confirmar exclusão</h2>
          <p>
            Deseja excluir o agendamento de <strong>{name}</strong>
            {appointmentDate ? ` às ${formatHHMM(appointmentDate)}` : ""}?
          </p>
          <span>Essa ação não poderá ser desfeita.</span>
        </div>

        <div className="sch-modal__actions">
          <button
            className="sch-btn sch-btn--ghost"
            type="button"
            onClick={onClose}
            disabled={deleting}
          >
            Cancelar
          </button>

          <button
            className="sch-btn sch-btn--danger"
            type="button"
            onClick={onConfirm}
            disabled={deleting}
          >
            {deleting ? "Excluindo..." : "Excluir"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SendReminderModal({
  open,
  appointment,
  sending,
  onClose,
  onConfirm,
}) {
  if (!open) return null;

  const appointmentDate = parseApiDate(appointment);
  const clientName = getClientName(appointment);

  return (
    <div className="sch-modal-overlay" role="dialog" aria-modal="true">
      <div className="sch-modal sch-modal--reminder">
        <div
          className="sch-modal__icon sch-modal__icon--reminder"
          aria-hidden="true"
        >
          <Mail size={23} />
        </div>

        <div className="sch-modal__body">
          <h2>Enviar lembrete agora</h2>

          <p>
            O lembrete será enviado imediatamente para{" "}
            <strong>{clientName}</strong>
            {appointmentDate
              ? ` em ${formatDateBR(appointmentDate)} às ${formatHHMM(
                  appointmentDate
                )}`
              : ""}
            .
          </p>

          <div className="sch-reminder-options">
            <div>
              <span>
                <strong>Paciente</strong>
                <small>
                  O e-mail mantém o link seguro de confirmação. No WhatsApp,
                  o cliente confirma ou informa que não poderá comparecer pelos botões da mensagem.
                </small>
              </span>
            </div>
          </div>
        </div>

        <div className="sch-modal__actions">
          <button
            className="sch-btn sch-btn--ghost-dark"
            type="button"
            onClick={onClose}
            disabled={sending}
          >
            Cancelar
          </button>

          <button
            className="sch-btn sch-btn--reminder"
            type="button"
            onClick={onConfirm}
            disabled={sending}
          >
            {sending ? "Enviando..." : "Enviar lembrete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleSkeleton() {
  return (
    <div className="sch-skeleton-list" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="sch-skeleton-card" key={`schedule-skeleton-${index}`}>
          <div className="sch-skeleton-avatar" />
          <div className="sch-skeleton-lines">
            <span />
            <small />
          </div>
          <div className="sch-skeleton-time" />
        </div>
      ))}
    </div>
  );
}

export default function ScheduleScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lembreteConfigOpen, setLembreteConfigOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const noticeRef = useRef(null);
  const [notice, setNotice] = useState(null);
  const [allAppointments, setAllAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [query, setQuery] = useState("");
  const [agendaFilter, setAgendaFilter] = useState("todos");
  const [confirmDel, setConfirmDel] = useState({
    open: false,
    appointment: null,
  });
  const [deleting, setDeleting] = useState(false);
  const [sendReminderModal, setSendReminderModal] = useState({
    open: false,
    appointment: null,
  });
  const [sendingReminderId, setSendingReminderId] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState({
    carregando: true,
    conectado: false,
    operacional: false,
    requerReconexao: false,
    erro: "",
  });
  const [syncingGoogleId, setSyncingGoogleId] = useState("");


  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  useEffect(() => {
    let ativo = true;

    async function carregarGoogleStatus() {
      const token = readAuth(AUTH_KEYS.TOKEN);
      const status = await fetchGoogleCalendarStatus(token);

      if (ativo) {
        setGoogleCalendarStatus({
          ...status,
          carregando: false,
          conectado: Boolean(status?.conectado),
          operacional: status?.operacional === true,
          requerReconexao: Boolean(status?.requerReconexao),
          erro: status?.erro || status?.ultimoErro || "",
        });
      }
    }

    carregarGoogleStatus();

    return () => {
      ativo = false;
    };
  }, []);


  const showNotice = useCallback((payload) => {
    setNotice({
      type: payload.type || "info",
      title: payload.title || getNoticeTitle(payload.type || "info"),
      message: payload.message,
      actionLabel: payload.actionLabel,
      onAction: payload.onAction,
      id: Date.now(),
    });

    window.requestAnimationFrame(() => {
      noticeRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      showNotice({
        type: "warning",
        title: "Sessão expirada",
        message: "Faça login novamente para continuar usando a agenda.",
        actionLabel: "Ir para login",
        onAction: () => navigate("/login", { replace: true }),
      });
      return null;
    }

    return { token, userId };
  }, [navigate, showNotice]);

  const fetchAppointments = useCallback(
    async ({ silent = false } = {}) => {
      const auth = authOrRedirect();
      if (!auth) return;

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetch(
          `${API_BASE}/psy/schedule`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        const data = await response.json().catch(() => null);

        if (isAuthenticationFailure(response, data)) {
          showNotice({
            type: "warning",
            title: "Sessão expirada",
            message: "Faça login novamente para atualizar sua agenda.",
            actionLabel: "Ir para login",
            onAction: () => navigate("/login", { replace: true }),
          });
          return;
        }

        if (response.status === 404) {
          setAllAppointments([]);
          setLastUpdatedAt(new Date());
          return;
        }

        if (!response.ok) {
          showNotice({
            type: "warning",
            title: "Não foi possível carregar a agenda",
            message:
              getApiErrorMessage(
                data,
                "Tente atualizar novamente em instantes."
              ),
            actionLabel: "Tentar novamente",
            onAction: () => fetchAppointments({ silent: true }),
          });
          setAllAppointments([]);
          return;
        }

        setAllAppointments(Array.isArray(data) ? data : []);
        setLastUpdatedAt(new Date());
      } catch (error) {
        console.error("Erro ao buscar agendamentos:", error);
        showNotice({
          type: "warning",
          title: "Falha de comunicação",
          message: "Não conseguimos conversar com a API agora. Verifique sua conexão e tente novamente.",
          actionLabel: "Tentar novamente",
          onAction: () => fetchAppointments({ silent: true }),
        });
        setAllAppointments([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, navigate, showNotice]
  );

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    const selectedDateFromState =
      String(location.state?.selectedDate || "").trim();

    if (selectedDateFromState) {
      const [year, month, day] =
        selectedDateFromState
          .split("-")
          .map(Number);

      if (year && month && day) {
        setSelectedDate(
          new Date(
            year,
            month - 1,
            day
          )
        );
      }
    }

    if (
      location.state?.refresh ||
      selectedDateFromState
    ) {
      if (location.state?.refresh) {
        fetchAppointments({
          silent: true,
        });
      }

      navigate(
        location.pathname,
        {
          replace: true,
          state: {},
        }
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const selectedISO = useMemo(() => toISODate(selectedDate), [selectedDate]);

  const dayAppointments = useMemo(() => {
    return (allAppointments || [])
      .filter((appointment) => {
        const date = parseApiDate(appointment);
        return date && toISODate(date) === selectedISO;
      })
      .sort((a, b) => {
        const dateA = parseApiDate(a)?.getTime() || 0;
        const dateB = parseApiDate(b)?.getTime() || 0;
        return dateA - dateB;
      });
  }, [allAppointments, selectedISO]);

  const stats = useMemo(() => {
    const total = dayAppointments.length;

    const encerrados = dayAppointments.filter(
      (appointment) =>
        getAppointmentStatus(appointment) === "baixado"
    ).length;

    const cancelados = dayAppointments.filter(
      (appointment) =>
        getAppointmentStatus(appointment) === "cancelado"
    ).length;

    const emAberto = dayAppointments.filter(
      (appointment) =>
        isAppointmentOpen(appointment)
    ).length;

    const confirmadas = dayAppointments.filter(
      (appointment) =>
        isAppointmentOpen(appointment) &&
        getClientConfirmationState(appointment).confirmado
    ).length;

    const proximo = dayAppointments.find((appointment) => {
      const date = parseApiDate(appointment);

      return (
        date &&
        date >= new Date() &&
        isAppointmentOpen(appointment)
      );
    });

    return {
      total,
      encerrados,
      cancelados,
      emAberto,
      confirmadas,
      proximo,
    };
  }, [dayAppointments]);

  const filteredAppointments = useMemo(() => {
    const q = normalizeText(query);

    return dayAppointments.filter((appointment) => {
      const operationalStatus =
        getOperationalStatus(appointment);

      const confirmation =
        getClientConfirmationState(appointment);

      const matchAgendaFilter =
        agendaFilter === "todos" ||
        (agendaFilter === "abertos" &&
          operationalStatus.key === "aberto") ||
        (agendaFilter === "confirmados" &&
          operationalStatus.key === "aberto" &&
          confirmation.confirmado) ||
        (agendaFilter === "encerrados" &&
          operationalStatus.key === "encerrado");

      if (!matchAgendaFilter) {
        return false;
      }

      if (!q) {
        return true;
      }

      const haystack = normalizeText(
        [
          getClientName(appointment),
          getServiceName(appointment),
          operationalStatus.label,
          getStatusLabel(
            getAppointmentStatus(appointment)
          ),
          confirmation.badge,
          appointment?.observacoes,
          appointment?.clienteRespostaConfirmacao,
          appointment?.clienteRespostaConfirmacaoVia,
        ]
          .filter(Boolean)
          .join(" ")
      );

      return haystack.includes(q);
    });
  }, [agendaFilter, dayAppointments, query]);

  const selectedDateLabel = useMemo(() => {
    if (isSameLocalDay(selectedDate, new Date())) {
      return "Hoje";
    }

    return formatLongDateBR(selectedDate);
  }, [selectedDate]);

  const goPreviousDay = () => {
    setSelectedDate((current) => addDays(current, -1));
  };

  const goNextDay = () => {
    setSelectedDate((current) => addDays(current, 1));
  };

  const goToday = () => {
    setSelectedDate(new Date());
  };

  const goNew = () => {
    navigate("/schedule/new");
  };

  const openLembreteConfig = () => {
    setLembreteConfigOpen(true);
  };

  const closeLembreteConfig = () => {
    setLembreteConfigOpen(false);
  };

  const handleLembreteConfigSaved = () => {
    showNotice({
      type: "success",
      title: "Configuração salva",
      message:
        "A régua de lembretes foi atualizada. Os próximos agendamentos usarão essa configuração.",
    });
  };

  const goEdit = (appointment) => {
    navigate("/schedule/edit", { state: { appointment } });
  };

  const openSessionControl = (appointment) => {
    const status = getAppointmentStatus(appointment);

    if (status === "baixado") {
      showNotice({
        type: "info",
        title: "Sessão já registrada",
        message: "Este agendamento já possui uma sessão encerrada.",
      });
      return;
    }

    if (status === "cancelado") {
      showNotice({
        type: "warning",
        title: "Agendamento cancelado",
        message: "Agendamentos cancelados não podem ser encerrados.",
      });
      return;
    }

    navigate("/session-control", { state: { appointment } });
  };


  const canSendReminder = (appointment) => {
    const status = getAppointmentStatus(appointment);
    const confirmation = getClientConfirmationState(appointment);

    if (confirmation.confirmado || confirmation.naoComparecera) {
      return false;
    }

    return !["cancelado", "baixado", "expirado", "no_show"].includes(status);
  };

  const openSendReminderModal = (appointment) => {
    if (!appointment?._id) {
      showNotice({
        type: "warning",
        title: "Agendamento inválido",
        message:
          "Não foi possível identificar este agendamento para enviar o lembrete.",
      });
      return;
    }

    if (!canSendReminder(appointment)) {
      const confirmation = getClientConfirmationState(appointment);

      showNotice({
        type: "warning",
        title: "Envio não permitido",
        message: confirmation.confirmado
          ? "O cliente já confirmou a presença. Os próximos lembretes de confirmação foram encerrados."
          : confirmation.naoComparecera
            ? "O cliente informou que não poderá comparecer. Avalie se o atendimento deve ser cancelado ou reagendado."
            : "Não é possível enviar lembrete para um agendamento cancelado, encerrado, expirado ou marcado como no-show.",
      });
      return;
    }

    setSendReminderModal({
      open: true,
      appointment,
    });
  };

  const closeSendReminderModal = () => {
    if (sendingReminderId) return;

    setSendReminderModal({
      open: false,
      appointment: null,
    });
  };

  const sendReminderNow = async () => {
    const appointment = sendReminderModal.appointment;
    const appointmentId = appointment?._id;

    if (!appointmentId) {
      setSendReminderModal({
        open: false,
        appointment: null,
      });
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    setSendingReminderId(appointmentId);

    try {
      const response = await fetch(
        `${API_BASE}/psy/lembrete-sessao/${encodeURIComponent(
          appointmentId
        )}/enviar`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            destinatario: "CLIENTE",
          }),
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      const data = await response.json().catch(() => ({}));

      /*
       * 403 não significa necessariamente sessão expirada.
       * Ex.: TOKEN_USER_MISMATCH, plano sem permissão ou regra de negócio.
       * Só tratamos como sessão expirada quando o retorno realmente for
       * de autenticação.
       */
      if (isAuthenticationFailure(response, data)) {
        setSendReminderModal({
          open: false,
          appointment: null,
        });

        showNotice({
          type: "warning",
          title: "Sessão expirada",
          message: "Faça login novamente para enviar lembretes.",
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      if (!response.ok) {
        setSendReminderModal({
          open: false,
          appointment: null,
        });

        const message = getApiErrorMessage(
          data,
          "Não foi possível enviar o lembrete agora."
        );

        const notify =
          response.status === 403 ? toast.warning : toast.error;

        notify({
          title: "Não foi possível enviar o lembrete",
          message,
        });
        return;
      }

      const feedback = buildCommunicationFeedback(
        data,
        data?.message || "Lembrete processado pelos canais disponíveis."
      );

      const notify = toast[feedback.tone] || toast.success;

      notify({
        title:
          feedback.tone === "warning"
            ? "Lembrete enviado com atenção"
            : feedback.tone === "error"
              ? "Falha no envio do lembrete"
              : "Lembrete enviado",
        message:
          feedback.message ||
          data?.message ||
          "Lembrete processado pelos canais disponíveis.",
        details: feedback.details,
      });

      setSendReminderModal({
        open: false,
        appointment: null,
      });

      /*
       * Atualiza a agenda silenciosamente para refletir eventual alteração
       * de status/confirmacão retornada pelo backend.
       */
      fetchAppointments({ silent: true });
    } catch (error) {
      console.error("Erro ao enviar lembrete manual:", error);

      toast.warning({
        title: "Falha de comunicação",
        message:
          error?.message ||
          "Não foi possível conversar com a API. O modal foi mantido aberto para permitir uma nova tentativa.",
      });
    } finally {
      setSendingReminderId("");
    }
  };

  const sincronizarGoogleAgora = async (appointment) => {
    const appointmentId = appointment?._id;

    if (!appointmentId || syncingGoogleId) {
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    if (googleCalendarStatus?.operacional !== true) {
      toast.warning({
        title: "Google Agenda requer atenção",
        message:
          googleCalendarStatus?.requerReconexao
            ? "Reconecte sua conta Google antes de tentar sincronizar este agendamento."
            : "Valide a conexão com o Google Agenda antes de tentar novamente.",
        actionLabel: "Abrir integrações",
        onAction: () => navigate("/integrations"),
      });
      return;
    }

    setSyncingGoogleId(appointmentId);

    try {
      const response = await fetch(
        `${API_BASE}/psy/integracoes/google/sincronizar/${encodeURIComponent(
          appointmentId
        )}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            Accept: "application/json",
          },
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      const data = await response.json().catch(() => ({}));

      if (isAuthenticationFailure(response, data)) {
        showNotice({
          type: "warning",
          title: "Sessão expirada",
          message: "Faça login novamente para sincronizar com o Google Agenda.",
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      if (!response.ok) {
        toast.warning({
          title: "Não foi possível sincronizar",
          message: getApiErrorMessage(
            data,
            "O Google Agenda não aceitou a sincronização deste agendamento."
          ),
          actionLabel:
            data?.requerReconexao === true
              ? "Abrir integrações"
              : undefined,
          onAction:
            data?.requerReconexao === true
              ? () => navigate("/integrations")
              : undefined,
        });
        return;
      }

      toast.success({
        title: "Google Agenda sincronizado",
        message:
          data?.message ||
          "O agendamento foi sincronizado com o Google Agenda.",
      });

      await fetchAppointments({ silent: true });
    } catch (error) {
      console.error("Erro ao sincronizar agendamento com o Google:", error);

      toast.warning({
        title: "Falha de comunicação",
        message:
          error?.message ||
          "Não foi possível conversar com a API para sincronizar o Google Agenda.",
      });
    } finally {
      setSyncingGoogleId("");
    }
  };


  const askDelete = (appointment) => {
    if (!appointment?._id) {
      showNotice({
        type: "warning",
        title: "Agendamento inválido",
        message: "Não foi possível identificar este agendamento para exclusão.",
      });
      return;
    }

    setConfirmDel({ open: true, appointment });
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setConfirmDel({ open: false, appointment: null });
  };

  const doDelete = async () => {
    const appointment = confirmDel.appointment;
    const id = appointment?._id;

    if (!id) {
      setConfirmDel({ open: false, appointment: null });
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    setDeleting(true);

    try {
      const response = await fetch(
        `${API_BASE}/psy/schedule/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      const data = await response.json().catch(() => null);

      if (isAuthenticationFailure(response, data)) {
        showNotice({
          type: "warning",
          title: "Sessão expirada",
          message: "Faça login novamente para excluir agendamentos.",
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      if (response.ok) {
        showNotice({
          type: "success",
          title: "Agendamento excluído",
          message: "O compromisso foi removido da agenda com sucesso.",
        });
        setConfirmDel({ open: false, appointment: null });
        fetchAppointments({ silent: true });
        return;
      }

      showNotice({
        type: "warning",
        title: "Não foi possível excluir",
        message: getApiErrorMessage(
          data,
          "Tente novamente em instantes."
        ),
      });
    } catch (error) {
      console.error("Erro ao excluir agendamento:", error);
      showNotice({
        type: "warning",
        title: "Falha de comunicação",
        message: "Não conseguimos conversar com a API agora. Tente novamente.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="sch-page">
      <div className="sch-bg" aria-hidden="true">
        <span className="sch-bg__orb sch-bg__orb--one" />
        <span className="sch-bg__orb sch-bg__orb--two" />
        <span className="sch-bg__orb sch-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="sch-topbar">
        <button
          className="sch-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="sch-brand">
          <img src={Logo} alt="PsyAssist" className="sch-brand__logo" />
          <div className="sch-brand__text">
            <strong>Agenda</strong>
            <span>Compromissos • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="sch-top-actions">
          <button
            className="sch-btn sch-btn--ghost sch-btn--desktop"
            type="button"
            onClick={openLembreteConfig}
          >
            <Settings2 size={16} aria-hidden="true" />
            Configurar lembretes
          </button>

          <button
            className="sch-btn sch-btn--ghost sch-btn--desktop"
            type="button"
            onClick={() => fetchAppointments({ silent: true })}
            disabled={loading || refreshing}
          >
            <RefreshCw
              size={16}
              aria-hidden="true"
              className={refreshing ? "sch-icon-spin" : ""}
            />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>

          <button
            className={`sch-btn sch-btn--calendar ${
              googleCalendarStatus.conectado &&
              googleCalendarStatus.operacional
                ? "sch-btn--calendar-on"
                : "sch-btn--calendar-off"
            } sch-btn--desktop`}
            type="button"
            onClick={() => navigate("/integrations")}
            title={
              googleCalendarStatus.erro ||
              (googleCalendarStatus.operacional
                ? "Google Agenda conectado e operacional"
                : "Abrir configurações do Google Agenda")
            }
          >
            <span aria-hidden="true">G</span>
            {googleCalendarStatus.carregando
              ? "Google..."
              : !googleCalendarStatus.conectado
                ? "Conectar Google"
                : googleCalendarStatus.requerReconexao
                  ? "Reconectar Google"
                  : googleCalendarStatus.operacional
                    ? "Google ativo"
                    : "Google com atenção"}
          </button>

          <button
            className="sch-btn sch-btn--primary"
            type="button"
            onClick={goNew}
          >
            <CalendarPlus size={17} aria-hidden="true" />
            Adicionar
          </button>
        </div>
      </header>

      <main className="sch-main">
        <div ref={noticeRef}>
          <NoticeBanner notice={notice} onClose={() => setNotice(null)} />
        </div>

        <section className="sch-hero">
          <div>
            <span className="sch-kicker">Gestão de agenda</span>
            <h1>Agenda</h1>
            <p>
              Organize seus compromissos, acompanhe sessões pendentes e acesse
              rapidamente o controle de atendimento.
            </p>
          </div>

          <aside className="sch-next-card">
            <small>
              <Clock3 size={14} aria-hidden="true" />
              Próximo compromisso
            </small>
            <strong>
              {stats.proximo
                ? `${formatHHMM(parseApiDate(stats.proximo))} • ${getClientName(
                    stats.proximo
                  )}`
                : "Sem próximos horários"}
            </strong>
            <span>
              {stats.proximo
                ? getServiceName(stats.proximo) || "Serviço não informado"
                : "Selecione outro dia ou crie um novo agendamento."}
            </span>
          </aside>
        </section>

        <section className="sch-stats">
          <article className="sch-stat sch-stat--primary">
            <span>Total do dia</span>
            <strong>{stats.total}</strong>
            <small>compromissos agendados</small>
          </article>

          <article className="sch-stat sch-stat--open">
            <span>Em aberto</span>
            <strong>{stats.emAberto}</strong>
            <small>sessões que ainda precisam ser encerradas</small>
          </article>

          <article className="sch-stat sch-stat--confirmed">
            <span>Confirmadas</span>
            <strong>{stats.confirmadas}</strong>
            <small>presenças confirmadas e ainda em aberto</small>
          </article>

          <article className="sch-stat sch-stat--closed">
            <span>Encerradas</span>
            <strong>{stats.encerrados}</strong>
            <small>sessões concluídas</small>
          </article>
        </section>

        <section className="sch-panel">
          <div className="sch-filter">
            <button
              className="sch-daybtn"
              type="button"
              onClick={goPreviousDay}
              aria-label="Dia anterior"
            >
              <ChevronLeft size={20} aria-hidden="true" />
            </button>

            <div className="sch-datebox">
              <span>{selectedDateLabel}</span>
              <strong>{formatDateBR(selectedDate)}</strong>
            </div>

            <button
              className="sch-daybtn"
              type="button"
              onClick={goNextDay}
              aria-label="Próximo dia"
            >
              <ChevronRight size={20} aria-hidden="true" />
            </button>

            <button
              className="sch-today"
              type="button"
              onClick={goToday}
            >
              <CalendarDays size={16} aria-hidden="true" />
              Hoje
            </button>

            <input
              className="sch-date-input"
              type="date"
              value={selectedISO}
              onChange={(event) => {
                const [year, month, day] = event.target.value.split("-").map(Number);
                if (!year || !month || !day) return;
                setSelectedDate(new Date(year, month - 1, day));
              }}
              aria-label="Selecionar data"
            />
          </div>

          <div className="sch-quick-filters" role="group" aria-label="Filtrar compromissos">
            <button
              type="button"
              className={`sch-quick-filter ${
                agendaFilter === "todos" ? "sch-quick-filter--active" : ""
              }`}
              onClick={() => setAgendaFilter("todos")}
            >
              Todos
              <strong>{stats.total}</strong>
            </button>

            <button
              type="button"
              className={`sch-quick-filter sch-quick-filter--open ${
                agendaFilter === "abertos" ? "sch-quick-filter--active" : ""
              }`}
              onClick={() => setAgendaFilter("abertos")}
            >
              Em aberto
              <strong>{stats.emAberto}</strong>
            </button>

            <button
              type="button"
              className={`sch-quick-filter sch-quick-filter--confirmed ${
                agendaFilter === "confirmados" ? "sch-quick-filter--active" : ""
              }`}
              onClick={() => setAgendaFilter("confirmados")}
            >
              Confirmadas
              <strong>{stats.confirmadas}</strong>
            </button>

            <button
              type="button"
              className={`sch-quick-filter sch-quick-filter--closed ${
                agendaFilter === "encerrados" ? "sch-quick-filter--active" : ""
              }`}
              onClick={() => setAgendaFilter("encerrados")}
            >
              Encerradas
              <strong>{stats.encerrados}</strong>
            </button>
          </div>

          <div className="sch-toolbar">
            <div>
              <h2>
                {agendaFilter === "abertos"
                  ? "Sessões em aberto"
                  : agendaFilter === "confirmados"
                    ? "Presenças confirmadas"
                    : agendaFilter === "encerrados"
                      ? "Sessões encerradas"
                      : "Compromissos do dia"}
              </h2>
              <p>
                {loading
                  ? "Carregando agenda..."
                  : `${filteredAppointments.length} de ${dayAppointments.length} compromisso(s) exibido(s)`}
              </p>
            </div>

            <div className="sch-search">
              <Search size={18} aria-hidden="true" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar cliente, serviço, situação ou confirmação..."
                aria-label="Buscar agendamento"
              />

              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Limpar busca"
                >
                  <X size={15} aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>

          {lastUpdatedAt ? (
            <div className="sch-updated">
              Atualizado em {formatDateBR(lastUpdatedAt)} às {formatHHMM(lastUpdatedAt)}
            </div>
          ) : null}

          {loading ? (
            <ScheduleSkeleton />
          ) : filteredAppointments.length === 0 ? (
            <div className="sch-state">
              <div className="sch-state__icon" aria-hidden="true">
                <CalendarDays size={27} />
              </div>
              <strong>
                {dayAppointments.length === 0
                  ? "Nenhum compromisso neste dia."
                  : agendaFilter === "abertos"
                    ? "Nenhuma sessão em aberto."
                    : agendaFilter === "confirmados"
                      ? "Nenhuma presença confirmada em aberto."
                      : agendaFilter === "encerrados"
                        ? "Nenhuma sessão encerrada."
                        : "Nenhum compromisso encontrado."}
              </strong>
              <span>
                {dayAppointments.length === 0
                  ? "Crie um novo agendamento para organizar sua agenda."
                  : agendaFilter !== "todos"
                    ? "Altere o filtro ou exiba todos os compromissos do dia."
                    : "Ajuste a busca para encontrar outro compromisso."}
              </span>

              <div className="sch-state__actions">
                {query ? (
                  <button
                    className="sch-btn sch-btn--ghost-dark"
                    type="button"
                    onClick={() => setQuery("")}
                  >
                    Limpar busca
                  </button>
                ) : null}

                {agendaFilter !== "todos" ? (
                  <button
                    className="sch-btn sch-btn--ghost-dark"
                    type="button"
                    onClick={() => setAgendaFilter("todos")}
                  >
                    Exibir todos
                  </button>
                ) : null}

                <button
                  className="sch-btn sch-btn--primary"
                  type="button"
                  onClick={goNew}
                >
                  <CalendarPlus size={17} aria-hidden="true" />
                  Adicionar agendamento
                </button>
              </div>
            </div>
          ) : (
            <div className="sch-list">
              {filteredAppointments.map((appointment) => {
                const date = parseApiDate(appointment);
                const mode = getAppointmentMode(appointment);
                const clientName = getClientName(appointment);
                const serviceName = getServiceName(appointment) || "Serviço não informado";
                const status = getAppointmentStatus(appointment);
                const operationalStatus = getOperationalStatus(appointment);
                const isClosed = operationalStatus.key === "encerrado";
                const isCanceled = operationalStatus.key === "cancelado";
                const googleSync = getGoogleSyncState(appointment);
                const clientConfirmation = getClientConfirmationState(appointment);
                const recurrence = getRecurrenceInfo(appointment);

                return (
                  <article
                    key={appointment?._id || `${clientName}-${date?.getTime()}`}
                    className={`sch-card ${isClosed ? "sch-card--closed" : ""} ${
                      isCanceled ? "sch-card--canceled" : ""
                    }`}
                  >
                    <button
                      className="sch-card__main"
                      type="button"
                      onClick={() => openSessionControl(appointment)}
                    >
                      <div className={`sch-avatar sch-avatar--${mode.tone}`}>
                        {mode.icon}
                      </div>

                      <div className="sch-info">
                        <div className="sch-title-row">
                          <strong className="sch-name">{clientName}</strong>
                          <span
                            className={`sch-badge sch-badge--operational sch-badge--${operationalStatus.tone}`}
                            title={operationalStatus.hint}
                          >
                            {operationalStatus.label}
                          </span>

                          {recurrence ? (
                            <span
                              className={`sch-badge sch-badge--recurrence ${
                                recurrence.exception
                                  ? "sch-badge--recurrence-exception"
                                  : ""
                              }`}
                              title={
                                recurrence.exception
                                  ? "Este horário é uma exceção dentro da série recorrente."
                                  : "Este agendamento pertence a uma série recorrente."
                              }
                            >
                              <Repeat2 size={12} aria-hidden="true" />
                              {recurrence.exception
                                ? `Exceção • ${recurrence.label}`
                                : recurrence.label}
                            </span>
                          ) : null}

                          {clientConfirmation.confirmado && !isClosed ? (
                            <span
                              className="sch-badge sch-badge--client-confirmed"
                              title={clientConfirmation.hint}
                            >
                              {clientConfirmation.badge}
                            </span>
                          ) : null}

                          {clientConfirmation.naoComparecera ? (
                            <span className="sch-badge sch-badge--warning">
                              {clientConfirmation.badge}
                            </span>
                          ) : null}
                        </div>

                        <div className="sch-sub">
                          <span>{serviceName}</span>
                          <span className="sch-dot" />
                          <span>{mode.label}</span>
                        </div>

                        <span
                          className={`sch-google-badge sch-google-badge--${googleSync.tone}`}
                          title={googleSync.title}
                        >
                          <b aria-hidden="true">G</b>
                          {googleSync.label}
                        </span>

                        {getGoogleMeetLink(appointment) ? (
                          <span
                            className="sch-meet-badge"
                            title="Sala do Google Meet disponível"
                          >
                            <Video size={14} aria-hidden="true" />
                            Google Meet
                          </span>
                        ) : null}

                        {isCanceled ? (
                          <div className="sch-canceled">Agendamento cancelado</div>
                        ) : null}
                      </div>

                      <div className="sch-side">
                        <small>Horário</small>
                        <strong className={`sch-time sch-time--${mode.tone}`}>
                          {formatHHMM(date)}
                        </strong>
                      </div>
                    </button>

                    <div className="sch-card__foot">
                      <div className="sch-hint">
                        {isClosed
                          ? "Sessão encerrada. O atendimento já foi registrado."
                          : isCanceled
                            ? "Este agendamento foi cancelado."
                            : status === "no_show"
                              ? "Cliente marcado como no-show."
                              : status === "expirado"
                                ? "Este compromisso expirou sem encerramento."
                                : clientConfirmation.naoComparecera
                                  ? clientConfirmation.hint
                                  : clientConfirmation.confirmado
                                    ? `${clientConfirmation.hint} A sessão continua em aberto até ser encerrada pelo psicólogo.`
                                    : "Sessão em aberto. Clique no card para encerrar o atendimento e registrar a sessão."}
                      </div>

                      <div className="sch-menu">
                        {getGoogleMeetLink(appointment) ? (
                          <button
                            className="sch-menubtn sch-menubtn--meet"
                            type="button"
                            onClick={() =>
                              window.open(
                                getGoogleMeetLink(appointment),
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            <ExternalLink size={14} aria-hidden="true" />
                            Entrar no Meet
                          </button>
                        ) : null}

                        {googleSync.tone === "error" ? (
                          <button
                            className="sch-menubtn"
                            type="button"
                            onClick={() => sincronizarGoogleAgora(appointment)}
                            disabled={
                              syncingGoogleId === appointment?._id ||
                              googleCalendarStatus?.operacional !== true
                            }
                            title={
                              googleCalendarStatus?.operacional === true
                                ? "Tentar sincronizar novamente com o Google Agenda"
                                : googleCalendarStatus?.requerReconexao
                                  ? "Reconecte o Google Agenda para sincronizar"
                                  : "A conexão com o Google Agenda precisa ser validada"
                            }
                          >
                            {syncingGoogleId === appointment?._id ? (
                              <>
                                <LoaderCircle
                                  size={14}
                                  aria-hidden="true"
                                  className="sch-icon-spin"
                                />
                                Sincronizando...
                              </>
                            ) : (
                              <>
                                <RefreshCw size={14} aria-hidden="true" />
                                Sincronizar Google
                              </>
                            )}
                          </button>
                        ) : null}

                        <button
                          className="sch-menubtn sch-menubtn--reminder"
                          type="button"
                          onClick={() => openSendReminderModal(appointment)}
                          disabled={
                            !canSendReminder(appointment) ||
                            sendingReminderId === appointment?._id
                          }
                          title={
                            clientConfirmation.confirmado
                              ? "O cliente já confirmou presença"
                              : clientConfirmation.naoComparecera
                                ? "O cliente informou que não poderá comparecer"
                                : canSendReminder(appointment)
                                  ? "Enviar um lembrete imediatamente"
                                  : "O status deste agendamento não permite o envio"
                          }
                        >
                          {sendingReminderId === appointment?._id ? (
                            <>
                              <LoaderCircle
                                size={14}
                                aria-hidden="true"
                                className="sch-icon-spin"
                              />
                              Enviando...
                            </>
                          ) : (
                            <>
                              <Send size={14} aria-hidden="true" />
                              Enviar lembrete
                            </>
                          )}
                        </button>

                        <button
                          className="sch-menubtn"
                          type="button"
                          onClick={() => goEdit(appointment)}
                        >
                          <Pencil size={14} aria-hidden="true" />
                          Editar
                        </button>

                        <button
                          className="sch-menubtn sch-menubtn--danger"
                          type="button"
                          onClick={() => askDelete(appointment)}
                        >
                          <Trash2 size={14} aria-hidden="true" />
                          Excluir
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <div className="sch-bottom-actions" aria-label="Ações rápidas da agenda">
        <button
          className="sch-bottom-actions__btn"
          type="button"
          onClick={goToday}
        >
          <CalendarDays size={17} aria-hidden="true" />
          Hoje
        </button>

        <button
          className="sch-bottom-actions__btn"
          type="button"
          onClick={openLembreteConfig}
        >
          <Settings2 size={17} aria-hidden="true" />
          Configurar
        </button>

        <button
          className="sch-bottom-actions__btn sch-bottom-actions__btn--primary"
          type="button"
          onClick={goNew}
        >
          <CalendarPlus size={17} aria-hidden="true" />
          Agendar
        </button>
      </div>

      <button
        className="sch-fab"
        type="button"
        onClick={goNew}
        aria-label="Adicionar"
      >
        <Plus size={24} aria-hidden="true" />
      </button>

      <SendReminderModal
        open={sendReminderModal.open}
        appointment={sendReminderModal.appointment}
        sending={Boolean(sendingReminderId)}
        onClose={closeSendReminderModal}
        onConfirm={sendReminderNow}
      />

      <LembreteSessaoConfigModal
        open={lembreteConfigOpen}
        onClose={closeLembreteConfig}
        onSaved={handleLembreteConfigSaved}
      />

      <ConfirmModal
        open={confirmDel.open}
        appointment={confirmDel.appointment}
        deleting={deleting}
        onClose={closeDeleteModal}
        onConfirm={doDelete}
      />
    </div>
  );
}
